/**
 * Activity 3 Summary page — donut chart for monarch survival outcomes.
 *
 * Chart.js is loaded as a separate vendor script (not bundled via Browserify).
 * Chart data is injected by PHP as window.mlmp_a3_chart.
 */
const init_nonsite_summary_table = require('../helpers/_nonsite-summary-table');

module.exports = function init_activity3_summary($) {

  const slugs = window.mlmp_settings || {};
  const on_nonsite_page = !!(slugs.page_slug_nonsite_a3_summary &&
    document.querySelector('body.body-' + slugs.page_slug_nonsite_a3_summary));

  init_nonsite_summary_table($, {
    back_link: on_nonsite_page ? slugs.nonsite_a3_summary_back_link : slugs.a3_summary_back_link,
    table_settings: (on_nonsite_page ? slugs.nonsite_a3_summary : slugs.a3_summary) || {},
  });

  // ── Monarch Survival donut chart ───────────────────────────────────────────
  const canvas = document.getElementById('a3-monarch-survival-chart');
  if (!canvas || typeof Chart === 'undefined' || typeof mlmp_a3_chart === 'undefined') return;

  const chart_data = mlmp_a3_chart;

  if (!chart_data.data || !chart_data.data.length) return;

  // Match the site's body font so chart text doesn't look out of place.
  const SITE_FONT = "'Inter', Helvetica, Arial, sans-serif";
  Chart.defaults.font.family = SITE_FONT;

  // Colors keyed by MLMP_LU_SurvivalResult code — matches the legacy site's
  // color scheme. Keying by code (rather than the label text) keeps this
  // mapping stable even if the lookup table labels are edited.
  const viz = (window.mlmp_settings || {}).viz_colors || {};
  const RESULT_COLORS = {
    1: viz.red,    // Parasitized by fly
    2: viz.navy,   // Parasitized by wasp
    3: viz.orange, // Dead from another cause
    4: viz.blue,   // Adult monarch
  };

  const bg_colors = (chart_data.codes || []).map(function (code) {
    return RESULT_COLORS[code] || viz.grey;
  });

  // Below this width, callouts have no room to render legibly — fall back to
  // percentages in the legend instead. Matches $media-sm in _variables.scss.
  const is_mobile = window.matchMedia('(max-width: 575px)').matches;

  // Draws "{label} ({percent}%)" callouts with leader lines pointing to each
  // non-empty slice — mirrors the legacy site's chart.
  const callouts_plugin = {
    id: 'a3Callouts',
    afterDraw: function (chart) {
      const meta = chart.getDatasetMeta(0);
      const dataset = chart.data.datasets[0];
      const total = dataset.data.reduce(function (a, b) { return a + b; }, 0);
      if (!total) return;

      const STUB = 8;    // horizontal clearance beyond the donut before the label
      const GAP = 6;     // horizontal gap between the line and the label text
      const MIN_SPACING = 18; // minimum vertical distance between stacked labels

      const left = [];
      const right = [];

      meta.data.forEach(function (arc, i) {
        const value = dataset.data[i];
        if (!value) return;

        const props = arc.getProps(['x', 'y', 'startAngle', 'endAngle', 'outerRadius'], true);
        const mid = (props.startAngle + props.endAngle) / 2;
        const cos = Math.cos(mid);
        const sin = Math.sin(mid);
        const pct = Math.round(value / total * 100);
        const side = cos < 0 ? -1 : 1;

        const item = {
          label: chart.data.labels[i] + ' (' + pct + '%)',
          edge: { x: props.x + cos * props.outerRadius, y: props.y + sin * props.outerRadius },
          // Bend point always clears the donut horizontally, regardless of
          // the slice's angle — keeps labels for slices near the top/bottom
          // from overlapping the donut.
          bend: { x: props.x + side * (props.outerRadius + STUB), y: props.y + sin * props.outerRadius },
        };

        (side < 0 ? left : right).push(item);
      });

      // Avoid overlapping labels on the same side by enforcing a minimum
      // vertical gap, working top to bottom.
      function space_out(items) {
        items.sort(function (a, b) { return a.bend.y - b.bend.y; });
        for (let i = 1; i < items.length; i++) {
          const min_y = items[i - 1].bend.y + MIN_SPACING;
          if (items[i].bend.y < min_y) items[i].bend.y = min_y;
        }
      }

      space_out(left);
      space_out(right);

      const ctx = chart.ctx;
      ctx.save();
      ctx.font = '12px ' + SITE_FONT;
      ctx.strokeStyle = viz.muted;
      ctx.fillStyle = viz.text;
      ctx.lineWidth = 1;
      ctx.textBaseline = 'middle';

      function draw_side(items, side) {
        items.forEach(function (item) {
          const label_x = side === 'left' ? item.bend.x - GAP : item.bend.x + GAP;

          ctx.beginPath();
          ctx.moveTo(item.edge.x, item.edge.y);
          ctx.lineTo(item.bend.x, item.bend.y);
          ctx.lineTo(label_x, item.bend.y);
          ctx.stroke();

          ctx.textAlign = side === 'left' ? 'right' : 'left';
          ctx.fillText(item.label, label_x, item.bend.y);
        });
      }

      draw_side(left, 'left');
      draw_side(right, 'right');

      ctx.restore();
    },
  };

  // On mobile there's no room for callout leader lines, so the legend picks
  // up the percentages instead.
  // Chart.js draws a bottom legend flush against the chart area, so an empty
  // legend "title" is used as a spacer to add a gap above it.
  const legend_options = {
    position: 'bottom',
    title: { display: true, text: '', padding: { top: 8 } },
  };
  if (is_mobile) {
    legend_options.labels = {
      generateLabels: function (chart) {
        const dataset = chart.data.datasets[0];
        const total = dataset.data.reduce(function (a, b) { return a + b; }, 0);
        return chart.data.labels.map(function (label, i) {
          const pct = total > 0 ? Math.round(dataset.data[i] / total * 100) : 0;
          return {
            text: label + ' (' + pct + '%)',
            fillStyle: dataset.backgroundColor[i],
            strokeStyle: dataset.backgroundColor[i],
            index: i,
          };
        });
      },
    };
  }

  new Chart(canvas, {
    type: 'doughnut',
    data: {
      labels: chart_data.labels,
      datasets: [{
        data: chart_data.data,
        backgroundColor: bg_colors,
      }],
    },
    plugins: is_mobile ? [] : [callouts_plugin],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: is_mobile
          ? { left: 10, right: 10, top: 10, bottom: 10 }
          : { left: 120, right: 120, top: 10, bottom: 10 },
      },
      plugins: {
        title: {
          display: true,
          text: chart_data.title || (chart_data.year + ' MLMP Monarch Survival for ' + chart_data.siteName),
          font: { size: is_mobile ? 13 : 15, weight: 'bold' },
          color: viz.title,
          padding: { bottom: 24 },
        },
        legend: legend_options,
        tooltip: {
          callbacks: {
            label: function (ctx) {
              const total = ctx.dataset.data.reduce(function (a, b) { return a + b; }, 0);
              const pct = total > 0 ? Math.round(ctx.parsed / total * 100) : 0;
              return ctx.label + ' (' + pct + '%)';
            },
          },
        },
      },
    },
  });

};
